export type EvWorkstationTourViewMode = "card" | "table";
export type EvWorkstationTourLane = "leads" | "in_touch";

export interface EvWorkstationTourStep {
  id: string;
  group: "intro" | "global" | "leads" | "in_touch" | "action_dock" | "table_fallback" | "complete";

  target?: string;

  title: string;
  subtitle?: string;

  content: {
    what: string;
    does: string;
    why: string;
    bestUse: string;
    outreachContext: string;
    backendImpact: string;
    avoid?: string;
  };

  requiredViewMode?: EvWorkstationTourViewMode;
  requiredLane?: EvWorkstationTourLane;

  openDetails?: string[];
  closeDetails?: string[];

  preferredPlacement?: "top" | "right" | "bottom" | "left" | "center";

  scrollBehavior?: {
    block?: ScrollLogicalPosition;
    inline?: ScrollLogicalPosition;
    extraTopPadding?: number;
  };

  fallbackTarget?: string;
  skipIfMissing?: boolean;
}

const READ_ONLY_STATE =
  "Selecting projects, switching lanes, changing frontend quick filters, opening details panels, and moving through the guide do not mutate backend data.";
const INTRO_CONTACTS =
  "Intro state changes update intro-contact/workflow data through /v1/ev-workstation/projects/:id/intro-contacts.";
const TELEGRAM_BACKEND =
  "Telegram auth/session state uses MTProto endpoints. Sending creates Telegram send/audit evidence. The guide never sends.";
const X_BACKEND =
  "X connection, preview, queue, send, thread, and audit surfaces use X-specific endpoints. Preview can create draft/audit state; send creates message/audit rows.";
const GOOGLE_BACKEND =
  "Google status reads connection/sync state. Refresh actions can queue Gmail/Calendar sync jobs.";

export const evWorkstationTourSteps: EvWorkstationTourStep[] = [
  // Intro and global UI
  {
    id: "welcome",
    group: "intro",
    preferredPlacement: "center",
    title: "Welcome to Ev's Workstation",
    content: {
      what: "This guide walks through the full BD operating surface for Ev's Workstation.",
      does: "It explains how to move through projects, manage active outreach, work warm intros, use contact intelligence, and understand what each action changes behind the scenes.",
      why: "The Workstation is not just a project list. It is the execution layer for outbound BD, warm-intro routing, follow-up discipline, communication context, and audit-safe operator actions.",
      bestUse:
        "Treat the Workstation as the source of truth for what should happen next with a project. Every field should answer one operational question: who owns this, what is the next move, when is it due, and through which channel?",
      outreachContext:
        "The Workstation keeps outreach from becoming memory-based. It turns every project into a repeatable workflow: qualify, route, contact, follow up, document, and hand off.",
      backendImpact: READ_ONLY_STATE,
      avoid:
        "Do not click live send buttons casually. The guide highlights send surfaces but blocks underlying interaction.",
    },
  },
  {
    id: "workstation-toolbar",
    group: "global",
    target: '[data-ev-tour="workstation-view-toolbar"]',
    title: "Workstation View toolbar",
    content: {
      what: "This is the top control strip for the Workstation.",
      does: "It lets the user see connection state, start this guide, and switch between the default card workflow and the older table fallback.",
      why: "The toolbar is the mode boundary. The card workflow is designed for day-to-day BD execution. The table fallback is useful for bulk scanning and inline editing.",
      bestUse:
        "Use Card view for active outreach and handoff work. Use Table fallback when scanning many rows, comparing filters, or editing queue fields in bulk.",
      outreachContext:
        "Card view keeps the operator focused on one project and one next move. Table view helps spot systemic backlog issues.",
      backendImpact:
        "Switching view modes is frontend state only. It does not mutate backend data.",
      avoid: "Do not confuse Card/Table view mode with the Leads/In Touch operational lanes.",
    },
  },
  {
    id: "google-compact-status",
    group: "global",
    target: '[data-ev-tour="google-compact-status"]',
    skipIfMissing: true,
    title: "Google communication status",
    content: {
      what: "This compact indicator summarizes Gmail and Calendar connection health for the Workstation.",
      does: "It tells the operator whether Google communication context is connected, disabled, misconfigured, revoked, or needs refresh.",
      why: "Gmail and Calendar context can inform call prep, daily brief priority, recent touchpoints, and relationship history.",
      bestUse:
        "When the badge says connected, treat Google-derived context as available but still verify the latest human state before sending anything. When it says reconnect or disabled, expect communication context to be stale or absent.",
      outreachContext:
        "Better relationship context means fewer awkward follow-ups and better continuity across emails, meetings, calls, and internal notes.",
      backendImpact: GOOGLE_BACKEND,
      avoid:
        "Do not assume missing communication context means no relationship exists. It may mean the Google feature is disabled or stale.",
    },
  },
  {
    id: "view-mode-buttons",
    group: "global",
    target: '[data-ev-tour="card-view-toggle"]',
    title: "Card workflow vs table fallback",
    content: {
      what: "These controls switch between the focused card workflow and the table fallback.",
      does: "Card mode shows one queue, one active project, and one action dock. Table mode shows the queue as rows with filters and inline controls.",
      why: "These are different operating styles. Card mode is for action. Table mode is for review.",
      bestUse:
        "Stay in Card view for daily execution. Use Table fallback when the operator needs broader queue visibility.",
      outreachContext:
        "Card mode reduces context switching and helps move one relationship forward at a time.",
      backendImpact: "The toggle does not write data. It only changes the rendered UI path.",
      avoid:
        "Do not implement the guide in table mode only. The default Workstation behavior is card mode.",
    },
  },
  {
    id: "lane-tabs",
    group: "global",
    target: '[data-ev-tour="lane-tabs"]',
    requiredViewMode: "card",
    title: "The two operational lanes",
    content: {
      what: "These are the two core Workstation tabs: Leads and In Touch.",
      does: "Leads contains unworked or lightly worked projects. In Touch contains projects already in active BD motion.",
      why: "The lanes separate discovery/qualification from active relationship management.",
      bestUse:
        "Start in Leads when deciding what deserves outreach. Move to In Touch when managing follow-ups, conversations, next actions, and active contact history.",
      outreachContext:
        "Leads asks, 'Should we work this and through whom?' In Touch asks, 'What is the next concrete move?'",
      backendImpact:
        "Switching lanes is frontend state. Actions inside each lane can update workflows, intro contacts, touchpoints, and events.",
      avoid:
        "Do not let a project sit in Leads once there is an active relationship or next action. That belongs in In Touch.",
    },
  },
  {
    id: "dashboard-grid",
    group: "global",
    target: '[data-ev-tour="dashboard-grid"]',
    requiredViewMode: "card",
    title: "The Workstation execution layout",
    content: {
      what: "Card mode is split into Queue Rail, Project Card, and Action Dock.",
      does: "The Queue Rail chooses the project, the Project Card drives the next workflow action, and the Action Dock manages contacts and messaging context.",
      why: "Each column has a separate job. This prevents the operator from mixing prioritization, project state, and contact/messaging execution into one overloaded panel.",
      bestUse:
        "Move left to right: pick the right project, decide the next action, then use the dock to find the right person or channel.",
      outreachContext: "This mirrors a clean BD motion: prioritize → decide → execute → record.",
      backendImpact:
        "Selecting a project is frontend state. Updating fields or sending messages triggers backend mutations.",
      avoid: "Do not use the Action Dock before confirming the correct active project is selected.",
    },
  },

  // Leads lane
  {
    id: "leads-tab",
    group: "leads",
    target: '[data-ev-tour="lane-tab-leads"]',
    requiredViewMode: "card",
    requiredLane: "leads",
    title: "Leads lane",
    content: {
      what: "Leads is the triage lane for projects that are not yet fully worked.",
      does: "It surfaces projects based on score, trend, warm lead state, missing outreach, and other BD signals.",
      why: "It keeps new or underworked opportunities from being lost in the broader database.",
      bestUse:
        "Use Leads to decide whether to ask for an intro, skip for now, mark NGMI, or move the project into a more active motion.",
      outreachContext:
        "Leads is where the operator asks, 'Is this worth starting, and what is the warmest path in?'",
      backendImpact:
        "Switching to Leads is read-only. Lead actions can update intro-contact status, workflow stage, follow-up date, next action, and event history.",
      avoid:
        "Do not treat every lead as equally actionable. The whole point is to separate warm, urgent, or high-potential projects from noise.",
    },
  },
  {
    id: "leads-queue-rail",
    group: "leads",
    target: '[data-ev-tour="queue-rail"]',
    requiredViewMode: "card",
    requiredLane: "leads",
    title: "Leads queue rail",
    content: {
      what: "This rail lists projects in the current lane.",
      does: "It lets the operator move through the lane one project at a time, while preserving the context of the broader queue.",
      why: "The rail prevents the operator from repeatedly going back to the full table just to choose the next project.",
      bestUse:
        "Work from the top unless there is a reason to filter. Use the rail like an execution queue, not a passive list.",
      outreachContext:
        "The rail is the daily call sheet for leads: who is worth attention, what is due, and where there may be a warm path.",
      backendImpact: "Selecting a queue item changes frontend selection only.",
      avoid:
        "Do not assume the visible list is the whole database. Filters and lane rules affect what appears.",
    },
  },
  {
    id: "project-search",
    group: "leads",
    target: '[data-ev-tour="project-search"]',
    requiredViewMode: "card",
    requiredLane: "leads",
    title: "Project search",
    content: {
      what: "This search filters the current project queue.",
      does: "It narrows projects by handle/name and other searchable project metadata.",
      why: "Search lets the operator jump directly to a project without leaving the Workstation.",
      bestUse:
        "Use search when coming from another surface, after a meeting, or when a founder/project name is already in mind.",
      outreachContext: "Search is for targeted work. The queue is for systematic work.",
      backendImpact:
        "Search feeds the Workstation project query filters. It fetches filtered data but does not mutate records.",
      avoid:
        "Do not leave a search applied and assume the lane is empty. Clear search before broad queue work.",
    },
  },
  {
    id: "quick-filter-chips",
    group: "leads",
    target: '[data-ev-tour="quick-filter-chips"]',
    requiredViewMode: "card",
    requiredLane: "leads",
    title: "Quick filter chips",
    content: {
      what: "These chips narrow the current lane by operational signals such as overdue, due today, warm lead, trending up, TGE hint, and missing next action.",
      does: "It lets the operator focus on one class of work.",
      why: "BD queues become unmanageable when urgent, warm, and stale items are mixed together.",
      bestUse:
        "Use 'warm lead' for intro-driven work, 'overdue' for cleanup, and 'trending up' when looking for new momentum.",
      outreachContext:
        "Filters help answer, 'What should I work right now?' instead of 'What exists?'",
      backendImpact:
        "These chips filter the rendered queue in frontend state. They do not write to the backend.",
      avoid:
        "Do not use quick filters as permanent status labels. They are views over existing flags.",
    },
  },
  {
    id: "queue-item",
    group: "leads",
    target: '[data-ev-tour="queue-item"]',
    requiredViewMode: "card",
    requiredLane: "leads",
    title: "Queue item cards",
    content: {
      what: "Each queue item is a condensed project summary.",
      does: "It shows the project identity, relevant follow-up date, and operational chips.",
      why: "The operator can decide what to open without reading the full project card.",
      bestUse:
        "Look for chips that indicate urgency or quality: warm lead, overdue, call today, TGE hint, or trending up.",
      outreachContext:
        "A queue item should tell the operator why the project deserves attention before opening it.",
      backendImpact: "Clicking a queue item selects it in frontend state only.",
      avoid: "Do not rely only on score. The chips often explain the operational reason to act.",
    },
  },
  {
    id: "lead-card-header",
    group: "leads",
    target: '[data-ev-tour="project-card-header"]',
    requiredViewMode: "card",
    requiredLane: "leads",
    title: "Lead card header",
    content: {
      what: "This is the active project identity block.",
      does: "It shows the project name, X handle, and quick access to Full detail and Call prep.",
      why: "It confirms which project every action on the page will affect.",
      bestUse:
        "Before saving or sending anything, verify this header matches the project you intend to work.",
      outreachContext:
        "This is the project context anchor. Every intro, message, and follow-up should be interpreted relative to this active project.",
      backendImpact:
        "Full detail and Call prep open project detail views. They do not by themselves mutate data.",
      avoid: "Do not work from the Action Dock without confirming this active project first.",
    },
  },
  {
    id: "score-and-chips",
    group: "leads",
    target: '[data-ev-tour="score-stack"]',
    requiredViewMode: "card",
    requiredLane: "leads",
    title: "Score, delta, and project chips",
    content: {
      what: "This area summarizes Alpha Score, score movement, category, workflow stage, TGE hints, and warm-lead state.",
      does: "It compresses scoring and BD context into quick decision signals.",
      why: "Lead prioritization should combine investment quality, momentum, and routeability.",
      bestUse:
        "A high score with a warm lead is strong. A lower score with strong momentum or a TGE hint may still deserve attention. No warm lead means the operator needs a different path.",
      outreachContext: "Think of these chips as the 'why now / why this' layer.",
      backendImpact:
        "These values come from project scoring, workflow state, and signal detection. Reading them is safe.",
      avoid:
        "Do not treat TGE hints as proof of token launch. They are signals, not archival triggers.",
    },
  },
  {
    id: "why-surfaced",
    group: "leads",
    target: '[data-ev-tour="why-surfaced"]',
    requiredViewMode: "card",
    requiredLane: "leads",
    title: "Why this lead surfaced",
    content: {
      what: "This section explains why the project is in front of the operator.",
      does: "It summarizes warm-lead, score, trend, call, or workflow signals.",
      why: "A good lead queue needs reasons, not just rankings.",
      bestUse:
        "Read this before deciding whether to ask for an intro or skip. It often tells you whether the project is urgent, warm, or simply interesting.",
      outreachContext:
        "This is the first pass at 'why would we reach out?' Use it to decide whether the outreach is justified.",
      backendImpact:
        "This is derived from the Workstation project payload and flags. No writes happen here.",
      avoid: "Do not overwrite the next action before understanding why the project surfaced.",
    },
  },
  {
    id: "lead-intro-section",
    group: "leads",
    target: '[data-ev-tour="lead-intro-section"]',
    requiredViewMode: "card",
    requiredLane: "leads",
    title: "Intro status and warm intro follow-up",
    content: {
      what: "This section tracks the warm-intro state for the lead.",
      does: "It shows whether an intro route exists, what status it is in, and when to follow up.",
      why: "Warm intros are often the difference between good outreach and cold noise.",
      bestUse:
        "If there is a strong intro contact, use Ask Intro and set a follow-up date. If there is no usable route, search contacts or skip.",
      outreachContext: "This section answers, 'Who can open the door?'",
      backendImpact: INTRO_CONTACTS,
      avoid:
        "Do not ask intros without adding a reason. The reason creates context for future follow-up.",
    },
  },
  {
    id: "lead-intro-list",
    group: "leads",
    target: '[data-ev-tour="lead-intro-list"]',
    requiredViewMode: "card",
    requiredLane: "leads",
    title: "Warm intro contact list",
    content: {
      what: "This list shows the best available relationship routes for the project.",
      does: "It displays contact name, fund/company, role, and available identity URL or handle.",
      why: "A warm path needs a real human and enough context to ask intelligently.",
      bestUse:
        "Prefer contacts with clear fund/company match and an available channel. If the route is weak, use the Action Dock search to find a better one.",
      outreachContext: "This is the 'relationship map' for the lead.",
      backendImpact:
        "Displayed contacts come from materialized relationship/contact data. Selecting or updating relationships through the Action Dock can change canonical contact records.",
      avoid:
        "Do not assume the first contact is always the best route. It is the first useful candidate, not an absolute recommendation.",
    },
  },
  {
    id: "lead-reason-note",
    group: "leads",
    target: '[data-ev-tour="lead-reason-note"]',
    requiredViewMode: "card",
    requiredLane: "leads",
    title: "Reason / note",
    content: {
      what: "This is the operator note field for the lead action.",
      does: "It records why an intro was asked, why the lead was skipped, or why it was marked not relevant.",
      why: "Future operators and agents need to understand the decision, not just the status.",
      bestUse:
        "Write short, concrete notes: 'Ask because they co-invested in X,' 'Skip because token already live,' 'No warm path and score weak.'",
      outreachContext: "Good notes reduce repeated work and prevent awkward duplicate asks.",
      backendImpact:
        "Notes can be persisted through workflow events or intro-contact updates, depending on the action.",
      avoid: "Do not leave important context only in your head.",
    },
  },
  {
    id: "lead-actions",
    group: "leads",
    target: '[data-ev-tour="lead-actions"]',
    requiredViewMode: "card",
    requiredLane: "leads",
    title: "Lead actions",
    content: {
      what: "These are the core decisions for a lead: Ask Intro, Next project, Skip / no for now, or Not relevant / NGMI.",
      does: "It lets the operator advance, defer, or remove the lead from active consideration.",
      why: "Leads need resolution. A lead queue that only grows becomes useless.",
      bestUse:
        "Ask Intro when there is a credible route. Skip when the project is not timely. Mark NGMI when it should not remain in the BD motion.",
      outreachContext:
        "This is the decision point: start a relationship, defer, or clear the queue.",
      backendImpact:
        "Ask Intro writes intro and workflow updates. Skip/NGMI changes workflow stage and clears next-action/follow-up fields as appropriate.",
      avoid: "Do not use NGMI for 'not today.' Use No for now when the project may matter later.",
    },
  },

  // In Touch lane
  {
    id: "in-touch-tab",
    group: "in_touch",
    target: '[data-ev-tour="lane-tab-in-touch"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    title: "In Touch lane",
    content: {
      what: "In Touch is the active BD relationship lane.",
      does: "It shows projects that already have motion: conversations, next actions, follow-up dates, group links, call context, or handoff preparation.",
      why: "Active relationships decay quickly without follow-up discipline.",
      bestUse:
        "Use this lane every day to resolve overdue follow-ups, update next actions, and document what happened.",
      outreachContext: "In Touch asks, 'What do we owe this project next?'",
      backendImpact: "Updating fields here changes workflow state and can append event history.",
      avoid: "Do not leave vague next actions like 'follow up.' Make the next action specific.",
    },
  },
  {
    id: "in-touch-queue",
    group: "in_touch",
    target: '[data-ev-tour="queue-rail"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    title: "Active relationship queue",
    content: {
      what: "This is the queue of projects already in BD motion.",
      does: "It prioritizes projects by follow-up, calls, missing actions, and activity state.",
      why: "Once a relationship starts, missing a follow-up can be worse than never reaching out.",
      bestUse:
        "Start with overdue and due-today items. Then work through missing next actions and call-today items.",
      outreachContext: "This queue is relationship maintenance, not discovery.",
      backendImpact:
        "Selecting a row is frontend state. Saving card fields writes workflow updates.",
      avoid:
        "Do not use the In Touch lane as a parking lot. Every active project should have a next action or a reason it is blocked.",
    },
  },
  {
    id: "in-touch-card-header",
    group: "in_touch",
    target: '[data-ev-tour="project-card"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    title: "In Touch project card",
    content: {
      what: "This is the main operating card for an active project.",
      does: "It combines project identity, status, follow-up timing, channel, next action, and notes.",
      why: "It keeps the operator focused on moving one relationship forward.",
      bestUse: "Read the current state, update only what changed, then record a clear next action.",
      outreachContext:
        "This card should answer: 'Where are we, what is the next move, and when does it happen?'",
      backendImpact: "Card saves go through Workstation workflow mutations.",
      avoid: "Do not edit several fields without saving. Treat it as a workflow update surface.",
    },
  },
  {
    id: "in-touch-why-surfaced",
    group: "in_touch",
    target: '[data-ev-tour="why-surfaced"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    title: "Why this active project surfaced",
    content: {
      what: "This explains the immediate operational reason the project is visible.",
      does: "It may show next action, call summary, overdue state, or another active signal.",
      why: "Active queue work should be driven by urgency and state, not by scrolling.",
      bestUse: "Use this as the first clue for what to update or execute.",
      outreachContext:
        "It tells the operator what relationship promise or blocker needs attention.",
      backendImpact: "This is read-only derived context.",
      avoid: "Do not overwrite state until the 'why surfaced' reason is understood.",
    },
  },
  {
    id: "project-follow-up-and-stage",
    group: "in_touch",
    target: '[data-ev-tour="in-touch-plan-section"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    title: "Follow-up date and stage",
    content: {
      what: "These fields track when the project needs attention and where it sits in the BD process.",
      does: "Follow-up date drives queue urgency. Stage records the project's workflow state.",
      why: "The queue cannot prioritize correctly without dates and stages.",
      bestUse:
        "Set a follow-up date whenever there is a promised next action. Update stage when the relationship materially moves forward.",
      outreachContext: "Dates protect relationship momentum. Stage protects pipeline truth.",
      backendImpact:
        "Saving writes workflow fields. Stage changes affect lane membership, table filters, and daily prioritization.",
      avoid: "Do not set a follow-up date without a next action.",
    },
  },
  {
    id: "last-contact-next-channel",
    group: "in_touch",
    target: '[data-ev-tour="last-contact-next-channel"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    title: "Last contact and next channel",
    content: {
      what: "These fields show when the last touch happened and what channel is expected next.",
      does: "It gives the operator immediate communication context.",
      why: "Outreach timing and channel discipline matter. A Telegram follow-up is not the same as an X DM or email follow-up.",
      bestUse:
        "Before sending, verify whether the next channel matches the relationship and current context.",
      outreachContext: "This helps avoid sending the right message through the wrong channel.",
      backendImpact:
        "Marking touchpoints can update lastContactAt, channel fields, workflow events, and message logs.",
      avoid: "Do not rely on memory for the last touch. Use the recorded state.",
    },
  },
  {
    id: "telegram-group-url",
    group: "in_touch",
    target: '[data-ev-tour="telegram-group-url"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    title: "Telegram DM/group URL",
    content: {
      what: "This field stores the Telegram route for the active project.",
      does: "It gives the Workstation a reusable channel for follow-ups and group context.",
      why: "Telegram is often the operational channel for crypto BD. Keeping the URL attached prevents lost context.",
      bestUse: "Store the canonical DM or group URL, not a vague note.",
      outreachContext: "This is the bridge from project workflow to actual contact channel.",
      backendImpact:
        "Saving this field updates workflow state. Sending through Telegram uses MTProto routes and creates send/audit evidence.",
      avoid:
        "Do not paste unrelated Telegram links. The URL should match the active project relationship.",
    },
  },
  {
    id: "next-action",
    group: "in_touch",
    target: '[data-ev-tour="next-action"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    title: "Next action",
    content: {
      what: "This is the single most important workflow field.",
      does: "It records the concrete next move for the project.",
      why: "Without a next action, the project can be 'active' but operationally dead.",
      bestUse:
        "Write an action that someone can execute without extra interpretation: 'Send deck follow-up to Alex by Friday,' not 'follow up.'",
      outreachContext: "A next action is a promise to the relationship.",
      backendImpact:
        "This field powers queue flags, daily brief logic, and operator prioritization.",
      avoid: "Do not write generic tasks. Be specific about person, channel, and intent.",
    },
  },
  {
    id: "internal-notes",
    group: "in_touch",
    target: '[data-ev-tour="internal-notes"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    title: "Internal notes",
    content: {
      what: "This is private operator context for the active project.",
      does: "It captures blockers, nuance, relationship state, and recent context that does not fit a structured field.",
      why: "Notes preserve judgment and reduce repeated discovery.",
      bestUse:
        "Keep notes concise and operational. Use them to explain why the next action or stage is what it is.",
      outreachContext: "Good notes help maintain tone and continuity across multiple touches.",
      backendImpact:
        "Notes persist with workflow state and can be used in later context-building or agent handoff.",
      avoid: "Do not put sensitive secrets or credentials here.",
    },
  },
  {
    id: "in-touch-actions",
    group: "in_touch",
    target: '[data-ev-tour="in-touch-actions"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    title: "In Touch action buttons",
    content: {
      what: "These buttons save workflow state or mark touchpoints.",
      does: "Update Status saves the current plan. Follow up / mark Telegram records a Telegram touch. Mark X touchpoint records an X touch. Next project advances the queue.",
      why: "Actions turn workflow intent into recorded operational state.",
      bestUse:
        "Save after edits. Mark touchpoints only when they actually happened or are intentionally being recorded.",
      outreachContext: "This is how the system knows the relationship moved.",
      backendImpact:
        "These buttons can update workflow fields and create workflow event entries. Send-specific actions elsewhere can also create message/audit rows.",
      avoid: "Do not mark contact just to clear the queue. That corrupts outreach history.",
    },
  },
  {
    id: "intro-history",
    group: "in_touch",
    target: '[data-ev-tour="intro-history"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    skipIfMissing: true,
    title: "Relationship source and intro history",
    content: {
      what: "This section shows the warm-intro or relationship source history for the active project.",
      does: "It helps the operator understand how the current relationship began.",
      why: "The route into a project often affects tone, leverage, and follow-up expectations.",
      bestUse: "Use it before re-engaging or asking for another intro.",
      outreachContext: "Relationship source is context. Context changes messaging.",
      backendImpact: "Intro history is derived from persisted intro/contact state.",
      avoid: "Do not overwrite intro source context with unrelated notes.",
    },
  },

  // Action Dock
  {
    id: "action-dock",
    group: "action_dock",
    target: '[data-ev-tour="action-dock"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    title: "Action Dock",
    content: {
      what: "The Action Dock is the contact, relationship, Telegram, X, and communication-context panel.",
      does: "It lets the operator search contacts, attach URLs, edit canonical contact details, send or preview messages, and inspect X history.",
      why: "The Project Card decides what to do. The Action Dock helps execute through the right person and channel.",
      bestUse:
        "Always start by selecting the correct active project and correct contact relationship.",
      outreachContext: "This is the execution panel for relationship-specific outreach.",
      backendImpact:
        "Actions here can update contacts, relationships, touchpoints, message logs, Telegram state, X prompts, X messages, drafts, and audit trails.",
      avoid:
        "Do not send from the dock unless the active project and selected contact are correct.",
    },
  },
  {
    id: "contact-search",
    group: "action_dock",
    target: '[data-ev-tour="contact-search-section"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    title: "Precise contact search",
    content: {
      what: "This search looks across known contact records and identities.",
      does: "It finds people by name, company, title, Telegram, X, LinkedIn, or email.",
      why: "Good outreach depends on the right person, not just the right project.",
      bestUse:
        "Search by company/fund when looking for intro routes. Search by handle or URL when resolving a specific identity.",
      outreachContext: "Use this to find the warmest credible route before sending cold outreach.",
      backendImpact: "Search reads contact/relationship data. It does not mutate records.",
      avoid: "Do not create duplicate contact context when an existing contact can be updated.",
    },
  },
  {
    id: "contact-scope-filter",
    group: "action_dock",
    target: '[data-ev-tour="contact-scope-filter"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    title: "Contact ownership scope",
    content: {
      what: "This filter separates company-wide contacts from personal contacts.",
      does: "It controls which contact ownership scope appears in search results.",
      why: "Some relationships belong to the company context; others are personal/operator-specific.",
      bestUse:
        "Use Company + mine for broad search. Use Personal when looking for Ev-owned private relationship paths.",
      outreachContext: "Ownership scope helps avoid misusing someone else's relationship.",
      backendImpact: "This changes query filters only.",
      avoid: "Do not assume all contacts are company-safe. Respect owner scope.",
    },
  },
  {
    id: "contact-results",
    group: "action_dock",
    target: '[data-ev-tour="contact-results"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    title: "Contact results",
    content: {
      what: "This list shows matching people and their relationship target.",
      does: "Selecting a contact loads the relationship for editing and messaging context.",
      why: "The selected relationship controls what thread, audit trail, and message targets are available.",
      bestUse:
        "Pick the contact whose target label matches the active project or intended relationship.",
      outreachContext:
        "Contact identity and relationship target both matter. A person may be relevant to multiple projects or entities.",
      backendImpact:
        "Selection is frontend state. Edits or sends after selection can mutate backend records.",
      avoid:
        "Do not send to a contact selected from an unrelated target without moving or attaching the relationship correctly.",
    },
  },
  {
    id: "contact-editor",
    group: "action_dock",
    target: '[data-ev-tour="contact-editor"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    title: "Contact detail editor",
    content: {
      what: "This editor updates canonical person and relationship metadata.",
      does: "It lets the operator adjust name, title, company, target label, owner scope, relationship follow-up date, and intro-contact status.",
      why: "Messaging and routing are only as good as the contact record.",
      bestUse: "Correct obvious stale fields before using the contact for outreach.",
      outreachContext:
        "A clean contact record prevents sloppy follow-ups and duplicate relationship work.",
      backendImpact:
        "Saving updates outreach relationship/person fields and invalidates contact queries.",
      avoid:
        "Do not change canonical identity fields unless the selected contact is definitely the same person.",
    },
  },
  {
    id: "contact-editor-actions",
    group: "action_dock",
    target: '[data-ev-tour="contact-editor-actions"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    title: "Contact editor actions",
    content: {
      what: "These buttons save contact details, move a relationship to the active project, or save a relationship follow-up date.",
      does: "It keeps relationship metadata aligned with the active project.",
      why: "Contacts frequently move from general relationship context into project-specific outreach context.",
      bestUse:
        "Use Move to active project when the selected contact is the right person but attached to the wrong target.",
      outreachContext:
        "This helps convert generic network knowledge into actionable project-specific BD state.",
      backendImpact: "These actions call outreach relationship endpoints and refresh contact data.",
      avoid:
        "Do not move a relationship just to make it visible. Move it only when the relationship genuinely belongs to the active project.",
    },
  },
  {
    id: "paste-contact-url",
    group: "action_dock",
    target: '[data-ev-tour="paste-contact-url"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    title: "Paste contact URL",
    content: {
      what: "This field captures Telegram, X, LinkedIn, or bare-handle identity input.",
      does: "It parses the URL or handle and attaches the identity to the selected/active relationship.",
      why: "Outreach identity capture must be fast and accurate.",
      bestUse:
        "Paste full URLs when possible. For bare handles, choose the correct platform before attaching.",
      outreachContext: "This is how a discovered person becomes reachable in the Workstation.",
      backendImpact:
        "Attaching identities can create or update Telegram/X/LinkedIn identity records and contact relationships.",
      avoid: "Do not attach a bare handle without resolving whether it is Telegram or X.",
    },
  },
  {
    id: "detected-contact-url",
    group: "action_dock",
    target: '[data-ev-tour="detected-contact-url"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    skipIfMissing: true,
    title: "Detected contact identity",
    content: {
      what: "This parsed output shows what platform and canonical URL the Workstation detected.",
      does: "It gives the operator a chance to verify before attaching.",
      why: "Misattached identities can cause outreach to the wrong person.",
      bestUse: "Confirm the detected platform, handle, and target label before saving.",
      outreachContext: "Identity hygiene protects trust.",
      backendImpact: "The actual attach action writes canonical identity data.",
      avoid:
        "Do not attach a URL just because it parsed. Verify it belongs to the selected contact.",
    },
  },
  {
    id: "telegram-send-section",
    group: "action_dock",
    target: '[data-ev-tour="telegram-send-section"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    title: "Telegram Send",
    content: {
      what: "This section sends or records Telegram follow-ups through the Workstation.",
      does: "It uses the saved Telegram route or manually entered Telegram URL plus message body.",
      why: "Telegram is a high-frequency BD channel, so send history needs to be auditable and project-scoped.",
      bestUse:
        "Confirm the active project, selected relationship, Telegram URL, and message body before sending.",
      outreachContext:
        "Telegram is usually direct and high-context. Keep messages specific and relationship-aware.",
      backendImpact: TELEGRAM_BACKEND,
      avoid: "Do not send from stale project context or unverified group URLs.",
    },
  },
  {
    id: "telegram-connection",
    group: "action_dock",
    target: '[data-ev-tour="telegram-connection-details"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    openDetails: ['[data-ev-tour="telegram-connection-details"]'],
    title: "Telegram connection",
    content: {
      what: "This collapsed section manages Telegram MTProto authentication.",
      does: "It tests credentials, starts auth, verifies code/2FA, and disconnects sessions.",
      why: "Telegram sends require a valid authenticated MTProto session.",
      bestUse:
        "Use this only during setup or reconnection. Day-to-day operators should not need to touch it if the session is healthy.",
      outreachContext:
        "Connection setup is infrastructure, not outreach. Keep it separate from messaging judgment.",
      backendImpact: "Auth actions can store encrypted session state and update connection status.",
      avoid: "Do not expose API hash, code, password, or session details in notes or screenshots.",
    },
  },
  {
    id: "telegram-message-fields",
    group: "action_dock",
    target: '[data-ev-tour="telegram-message-body"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    title: "Telegram URL and message body",
    content: {
      what: "These fields define where the Telegram message goes and what it says.",
      does: "The URL targets the DM/group. The body is the actual outbound message.",
      why: "Message context and recipient correctness are the highest-risk part of outreach.",
      bestUse: "Write short, specific follow-ups. Reference the actual relationship context.",
      outreachContext:
        "The best Telegram follow-up should be easy to answer and clearly tied to the prior interaction.",
      backendImpact: "Sending records the message, status, and audit evidence.",
      avoid: "Do not send generic cold-copy from a project-specific relationship panel.",
    },
  },
  {
    id: "x-dm-section",
    group: "action_dock",
    target: '[data-ev-tour="x-dm-section"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    title: "X DM Preview / Send",
    content: {
      what: "This section handles X connection status, handle resolution, DM preview, and DM send.",
      does: "It lets the operator preview a message, check similarity safety, then send if allowed.",
      why: "X DMs are easy to duplicate or over-automate. Preview and audit controls reduce that risk.",
      bestUse:
        "Always preview before sending. Edit the draft if the system flags similarity or repair needed.",
      outreachContext:
        "X is a lower-context channel than Telegram. Messages need to be concise and differentiated.",
      backendImpact: X_BACKEND,
      avoid: "Do not bypass preview/similarity warnings.",
    },
  },
  {
    id: "x-handle-resolve",
    group: "action_dock",
    target: '[data-ev-tour="selected-x-handle"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    title: "Selected X handle",
    content: {
      what: "This shows the X handle available for the selected contact.",
      does: "It lets the operator confirm or resolve the handle before drafting/sending.",
      why: "The right project with the wrong contact handle is still a bad send.",
      bestUse: "Resolve the selected X handle when identity confidence is unclear.",
      outreachContext: "Identity verification is part of respectful outbound.",
      backendImpact: "Resolve calls X handle resolution logic. It does not send a message.",
      avoid: "Do not send to a handle that belongs to a different person or organization.",
    },
  },
  {
    id: "x-dm-body-preview",
    group: "action_dock",
    target: '[data-ev-tour="x-dm-body"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    title: "X DM body and preview",
    content: {
      what: "This is the editable DM body and preview flow.",
      does: "It lets the operator generate or write a message, then preview it for safety and similarity checks.",
      why: "Repeated or overly similar DMs hurt outreach quality and can trigger safeguards.",
      bestUse: "Use the preview as a required review checkpoint. Make material edits when flagged.",
      outreachContext:
        "A good X DM should be specific to the person/project, short, and non-generic.",
      backendImpact: "Preview can create draft/audit records and return similarity flags.",
      avoid: "Do not send if the body is still identical to a blocked preview.",
    },
  },
  {
    id: "x-send-button",
    group: "action_dock",
    target: '[data-ev-tour="x-send-button"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    title: "Send X DM",
    content: {
      what: "This is the final X DM send action.",
      does: "It attempts to send the selected/previewed X DM through the connected account.",
      why: "This is a live outreach action and must be treated as high-stakes.",
      bestUse:
        "Confirm active project, selected relationship, selected handle, body, preview status, and live-send gate before sending.",
      outreachContext: "A sent DM becomes part of the relationship record.",
      backendImpact:
        "Successful sends create X message records and audit rows, then refresh thread/audit queries.",
      avoid: "Do not click Send during the guide. The overlay blocks it.",
    },
  },
  {
    id: "x-thread-audit",
    group: "action_dock",
    target: '[data-ev-tour="x-thread-audit-details"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    openDetails: ['[data-ev-tour="x-thread-audit-details"]'],
    title: "X thread and audit history",
    content: {
      what: "This section shows recent X thread messages and audit events.",
      does: "It gives visibility into prior messages, statuses, send sources, and errors.",
      why: "Operators need to know what was already sent before sending again.",
      bestUse: "Review thread and audit history before drafting another DM.",
      outreachContext: "Avoid duplicate outreach and maintain conversational continuity.",
      backendImpact:
        "This reads X message and audit endpoints scoped to the selected relationship.",
      avoid: "Do not send without checking history when the relationship is already active.",
    },
  },
  {
    id: "x-settings-queue",
    group: "action_dock",
    target: '[data-ev-tour="x-settings-queue-details"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    openDetails: ['[data-ev-tour="x-settings-queue-details"]'],
    title: "X settings and queue",
    content: {
      what: "This section manages the X prompt template, generated examples, queue contacts, and draft queue.",
      does: "It controls how generated X DMs are shaped and how previewed drafts are reviewed, sent, discarded, or repaired.",
      why: "Prompt settings and queue review keep automated outreach aligned with the operator's style and safety requirements.",
      bestUse:
        "Save prompt guidance before generating examples. Review drafts before sending. Repair or discard weak drafts.",
      outreachContext: "Automation should help write better outreach, not create careless volume.",
      backendImpact:
        "Settings write prompt configuration. Queue preview/send/repair/discard updates draft and audit state.",
      avoid:
        "Do not use generated examples as proof that a message is ready. They are starting points.",
    },
  },
  {
    id: "todays-brief",
    group: "action_dock",
    target: '[data-ev-tour="today-brief-details"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    openDetails: ['[data-ev-tour="today-brief-details"]'],
    title: "Today's Brief",
    content: {
      what: "This section embeds the daily BD brief into the Action Dock.",
      does: "It summarizes overdue items, due-today items, missing next actions, warm leads needing outreach, and active call-prep items.",
      why: "The daily brief is the 'what needs attention today?' layer.",
      bestUse:
        "Use it at the start of a session to decide where to focus before manually browsing the queue.",
      outreachContext: "It turns scattered workflow signals into a daily operating plan.",
      backendImpact:
        "The brief reads from Workstation state and can support priority actions such as snooze, resolve, or block through dedicated mutations.",
      avoid:
        "Do not ignore brief blockers. They often represent stale or incomplete workflow state.",
    },
  },
  {
    id: "connection-settings-status",
    group: "action_dock",
    target: '[data-ev-tour="connection-settings-details"]',
    requiredViewMode: "card",
    requiredLane: "in_touch",
    openDetails: ['[data-ev-tour="connection-settings-details"]'],
    title: "Connection and settings status",
    content: {
      what: "This section summarizes integration health, especially Google status in the current card workflow.",
      does: "It shows whether communication sync is enabled, connected, fresh, or stale.",
      why: "Many downstream surfaces depend on connected providers.",
      bestUse: "Check this when communication context looks missing or stale.",
      outreachContext:
        "Integration health affects how much relationship context the operator can trust.",
      backendImpact: "Refresh actions can queue provider sync jobs. Reading status is safe.",
      avoid: "Do not assume absent context means absent relationship history.",
    },
  },

  // Table fallback
  {
    id: "table-fallback-brief",
    group: "table_fallback",
    target: '[data-ev-tour="table-fallback-brief"]',
    requiredViewMode: "table",
    title: "Daily brief in table mode",
    content: {
      what: "The daily brief panel appears above the table when in table fallback mode.",
      does: "It summarizes overdue items, due-today actions, missing next actions, warm leads, and call-prep status.",
      why: "Even in table view, the operator needs a quick sense of what deserves attention today.",
      bestUse: "Scan the brief first, then use filters to narrow the table to the relevant subset.",
      outreachContext: "The brief turns table scanning into prioritized action.",
      backendImpact: "The brief reads Workstation state. It does not write data.",
      avoid: "Do not ignore the brief just because you are in table mode.",
    },
  },
  {
    id: "workflow-filters",
    group: "table_fallback",
    target: '[data-ev-tour="workflow-filters"]',
    requiredViewMode: "table",
    title: "Workflow filters",
    content: {
      what: "These filters narrow the table by search text, stage, priority, owner, score band, delta direction, and scope.",
      does: "They let the operator isolate a specific slice of the pipeline for review or bulk editing.",
      why: "Tables become unusable without filtering. Filters make bulk review actionable.",
      bestUse:
        "Combine filters to find systemic gaps: all overdue projects owned by Ev, or all high-score leads with no next action.",
      outreachContext: "Filters help answer systemic questions like 'Where is the pipeline stuck?'",
      backendImpact:
        "Filters are frontend query parameters. They fetch data but do not mutate records.",
      avoid: "Do not leave restrictive filters active and assume the queue is empty.",
    },
  },
  {
    id: "workflow-table",
    group: "table_fallback",
    target: '[data-ev-tour="workflow-table"]',
    requiredViewMode: "table",
    title: "Workflow table",
    content: {
      what: "The table shows every workstation project as a row with inline fields.",
      does: "It displays stage, follow-up dates, next action, intro status, warm intro follow-up, and score at a glance.",
      why: "Table view is useful for bulk scanning, comparing rows, and editing fields inline without opening each card.",
      bestUse:
        "Use the table for pipeline reviews, cleanup sessions, and spotting patterns across many projects.",
      outreachContext:
        "Table view reveals systemic outreach gaps: many projects with no next action, or stalled stages.",
      backendImpact:
        "Inline edits in the table call the same workflow mutation endpoints as the card view.",
      avoid:
        "Do not treat the table as the primary daily execution surface. Card view is designed for that.",
    },
  },
  {
    id: "workflow-table-row",
    group: "table_fallback",
    target: '[data-ev-tour="workflow-table-row"]',
    requiredViewMode: "table",
    title: "Table row actions",
    content: {
      what: "Each row represents one project and its current workflow state.",
      does: "Clicking a row opens the project detail. Some columns allow inline editing of stage, follow-up, and next action.",
      why: "Row-level editing reduces the friction of updating many projects in a single session.",
      bestUse:
        "Edit inline when the change is obvious and small. Open full detail when the change needs context.",
      outreachContext:
        "Keeping rows accurate ensures the card queue and daily brief are also accurate.",
      backendImpact: "Inline saves update workflow state and append events just like card actions.",
      avoid:
        "Do not bulk-edit without verifying each project's context. Table density hides nuance.",
    },
  },

  // Completion
  {
    id: "complete",
    group: "complete",
    preferredPlacement: "center",
    title: "Guide complete",
    content: {
      what: "The end of the guided tour.",
      does: "Returns the user to the Workstation state they had before starting the guide.",
      why: "The guide should educate without disrupting active work.",
      bestUse: "Re-run it any time from the Guide button.",
      outreachContext:
        "The Workstation is a loop: pick the right project, understand why it matters, decide the next action, execute through the right contact, and record what changed.",
      backendImpact:
        "The guide wrote no backend data. Only future operator actions will mutate state.",
      avoid:
        "Do not treat the guide as a substitute for reviewing actual project/contact state before sending.",
    },
  },
];

export function getVisibleSteps(hasProjects: boolean): EvWorkstationTourStep[] {
  if (!hasProjects) {
    return evWorkstationTourSteps.filter(
      (step) =>
        step.group === "intro" ||
        step.group === "global" ||
        step.group === "complete" ||
        step.id === "action-dock"
    );
  }
  return evWorkstationTourSteps;
}
